/**
 * TwinHome -- Operational Twin module home (P32.2).
 *
 * Replaces the v0.4.0 `HelloTwin` "this module is live!" stub with
 * a real module home following `docs/v2-platform-plan.md`:
 *
 *   "MARSAD v2 is one platform with three modules ...
 *    Home = three queues (your Preparer / Reviewer / Approver
 *    work) for the active module ... Per-module signals strip ...
 *    Lists = filtered queues, not exploratory tables."
 *
 * Twin doesn't have position-shaped work on the backend today
 * (cascade investigation is ad-hoc, not Preparer/Reviewer/Approver
 * queued), so we deliberately DO NOT fake position queue chips here.
 * They land when Twin gets real workflow (e.g. when #537 surfaces
 * a per-channel deriver that produces work items).
 *
 * What we DO have:
 *
 *   - A live signals strip from `useCascade()` -- impacted IBSes,
 *     critical count, MVB intersection -- the actual live state of
 *     the bank in motion.
 *   - 3 tile sections grouped by INTENT (not data type, not v1's
 *     "LANDSCAPE / RESILIENCE" data-bucket headings):
 *       STATE       -- what's happening right now
 *       ANALYSIS    -- where to look to understand it
 *       GOVERNANCE  -- exec view + audit surface
 *
 * Module-boundary rule (eslint): twin/* may import from twin/* and
 * shared/* only. Same as HelloTwin.
 */
import { useTranslation } from "react-i18next";
import { canReadReadiness } from "shared/api/authority";
import { canRunOperations } from "shared/api/authority";
import { canReadCeremony } from "shared/api/authority";
import { useMe } from "shared/api/useMe";
import { ModuleRubric, Tile, TileSection } from "shared/layout/ModuleHomeTile";
import { ViewRail } from "shared/onboarding/ViewRail";
import { useCascade } from "twin/api/hooks";

/**
 * SignalsStrip -- bank-wide live state from the cascade summary.
 *
 * Reads /v1/cascade once on mount + the existing 15s poll. When
 * data isn't available yet (loading or 503), renders a slim
 * "no signal" placeholder rather than nothing -- the strip
 * occupies a stable slot so the layout doesn't shift.
 */
function SignalsStrip() {
  const cascadeQ = useCascade();
  const s = cascadeQ.data;
  const { t } = useTranslation();
  /* Round 12 B1 -- "All clear" over a bank with nothing in it. Zero
     impacted because nothing is wrong and zero impacted because there
     is nothing to impact are the same zero, and this strip reported
     the second as the first, in emerald, on the Twin's front page. */
  const notAssessed = s != null && s.estate_assessed === false;
  const allClear = s != null && s.impacted_count === 0 && !notAssessed;
  return (
    <section
      data-testid="twin-signals-strip"
      data-state={
        notAssessed ? "not-assessed" : allClear ? "clear" : s ? "impacted" : "loading"
      }
      className={[
        "rounded-lg border px-4 py-3",
        notAssessed
          ? "border-slate-200 bg-slate-50"
          : allClear
            ? "border-emerald-200 bg-emerald-50"
            : s
              ? "border-rose-200 bg-rose-50"
              : "border-slate-200 bg-white",
      ].join(" ")}
    >
      <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1 text-sm">
        {!s && (
          <span className="text-slate-500 text-xs">
            {t("modules.twin.home.signals.loading")}
          </span>
        )}
        {notAssessed && (
          <span
            data-testid="twin-signals-not-assessed"
            className="font-semibold text-slate-700"
          >
            {t("modules.twin.home.signals.notAssessed")}
          </span>
        )}
        {s && allClear && (
          <span
            data-testid="twin-signals-allclear"
            className="text-emerald-900 font-semibold"
          >
            {t("modules.twin.home.signals.allClear")}
          </span>
        )}
        {s && !allClear && !notAssessed && (
          <>
            <span
              data-testid="twin-signals-impacted"
              className="text-rose-900 font-semibold"
            >
              {t("modules.twin.home.signals.impacted", { count: s.impacted_count })}
            </span>
            {(s.impacted_by_severity.critical ?? 0) > 0 && (
              <span data-testid="twin-signals-critical" className="text-rose-800">
                {t("modules.twin.home.signals.critical", {
                  count: s.impacted_by_severity.critical ?? 0,
                })}
              </span>
            )}
            {(s.impacted_by_severity.high ?? 0) > 0 && (
              <span data-testid="twin-signals-high" className="text-rose-700">
                {t("modules.twin.home.signals.high", {
                  count: s.impacted_by_severity.high ?? 0,
                })}
              </span>
            )}
            {s.product_count > 0 && (
              <span className="text-rose-700">
                {t("modules.twin.home.signals.products", { count: s.product_count })}
              </span>
            )}
            {s.mvb_total_count > 0 && (
              <span data-testid="twin-signals-mvb" className="text-indigo-800">
                {t("modules.twin.home.signals.mvb", {
                  impacted: s.mvb_impacted_count,
                  total: s.mvb_total_count,
                  pct: Math.round((s.mvb_impacted_share ?? 0) * 100),
                })}
              </span>
            )}
          </>
        )}
      </div>
    </section>
  );
}

export function TwinHome() {
  const me = useMe();
  const { t } = useTranslation();
  const isAdmin = me.data?.is_admin ?? false;
  /* Round 16 H16 -- the admission, function and MVB registers admit
     every ceremony reader (require_ceremony_reader), and the tab strip
     offered them to everyone, but the overview named them to
     administrators only: sixteen rounds of a Resilience Lead reading
     an overview without the two registers she runs. */
  const readsCeremony = canReadCeremony(me.data);

  return (
    <article data-testid="twin-home" className="max-w-6xl space-y-6">
      <ViewRail viewKey="twin" />
      <header>
        <ModuleRubric>{t("modules.twin.home.rubric")}</ModuleRubric>
        <h1
          data-testid="twin-home-heading"
          className="mt-1 font-display text-2xl font-semibold text-slate-900"
        >
          {t("modules.twin.home.heading")}
        </h1>
        <p
          data-testid="twin-home-positioning"
          className="mt-1 text-sm text-slate-700 max-w-3xl"
        >
          {t("modules.twin.home.positioning")}
        </p>
      </header>

      <SignalsStrip />

      <TileSection
        heading={t("modules.twin.home.sections.state")}
        testid="twin-section-state"
      >
        <Tile
          to="/twin/cascade"
          name={t("modules.twin.home.tiles.cascade.name")}
          desc={t("modules.twin.home.tiles.cascade.desc")}
          testid="twin-tile-cascade"
          icon="cascade"
        />
        <Tile
          to="/twin/channels"
          name={t("modules.twin.home.tiles.channels.name")}
          desc={t("modules.twin.home.tiles.channels.desc")}
          testid="twin-tile-channels"
          icon="capability-map"
        />
        <Tile
          to="/twin/telemetry"
          name={t("modules.twin.home.tiles.telemetry.name")}
          desc={t("modules.twin.home.tiles.telemetry.desc")}
          testid="twin-tile-telemetry"
          icon="telemetry"
        />
        <Tile
          to="/twin/mvb-target-state"
          name={t("modules.twin.home.tiles.mvbTargetState.name")}
          desc={t("modules.twin.home.tiles.mvbTargetState.desc")}
          testid="twin-tile-mvb-target-state"
          icon="mvb"
        />
        <Tile
          to="/twin/technology-currency"
          name={t("modules.twin.home.tiles.technologyCurrency.name")}
          desc={t("modules.twin.home.tiles.technologyCurrency.desc")}
          testid="twin-tile-technology-currency"
          icon="telemetry"
        />
        <Tile
          to="/twin/scenarios"
          name={t("modules.twin.home.tiles.scenarios.name")}
          desc={t("modules.twin.home.tiles.scenarios.desc")}
          testid="twin-tile-scenarios"
          icon="scenario"
        />
        <Tile
          to="/twin/journeys"
          name={t("modules.twin.home.tiles.journeys.name")}
          desc={t("modules.twin.home.tiles.journeys.desc")}
          testid="twin-tile-journeys"
          icon="journey"
        />
        {/* Round 11 F4 -- offered where the idea lives. The page is on
            the admin console and nowhere else, and edge propagation
            delay is a CASCADE concept: the reader who wants it is the
            one looking at the cascade, who has no route to it from
            here. The walk called it the best teaching of its idea in
            the product and reached it from nothing. */}
        <Tile
          to="/admin/edge-delays"
          name={t("modules.twin.home.tiles.edgeDelays.name")}
          desc={t("modules.twin.home.tiles.edgeDelays.desc")}
          testid="twin-tile-edge-delays"
          icon="cascade"
        />
      </TileSection>

      <TileSection
        heading={t("modules.twin.home.sections.analysis")}
        testid="twin-section-analysis"
      >
        {/* E3 -- what to do about what the twin is showing. */}
        <Tile
          to="/recommendations"
          name={t("modules.twin.home.tiles.recommendations.name")}
          desc={t("modules.twin.home.tiles.recommendations.desc")}
          testid="twin-tile-recommendations"
          icon="recommendation"
        />
        <Tile
          to="/twin/tolerance-metrics"
          name={t("modules.twin.home.tiles.toleranceMetrics.name")}
          desc={t("modules.twin.home.tiles.toleranceMetrics.desc")}
          testid="twin-tile-tolerance-metrics"
          icon="tolerance-clock"
        />
        <Tile
          to="/twin/continuity-plans"
          name={t("modules.twin.home.tiles.continuityPlans.name")}
          desc={t("modules.twin.home.tiles.continuityPlans.desc")}
          testid="twin-tile-continuity-plans"
          icon="drill"
        />
        <Tile
          to="/twin/changes"
          name={t("modules.twin.home.tiles.changes.name")}
          desc={t("modules.twin.home.tiles.changes.desc")}
          testid="twin-tile-changes"
          icon="journey"
        />
        <Tile
          to="/twin/architecture-standards"
          name={t("modules.twin.home.tiles.architectureStandards.name")}
          desc={t("modules.twin.home.tiles.architectureStandards.desc")}
          testid="twin-tile-architecture-standards"
          icon="capability-map"
        />
        <Tile
          to="/twin/service-disposition"
          name={t("modules.twin.home.tiles.serviceDisposition.name")}
          desc={t("modules.twin.home.tiles.serviceDisposition.desc")}
          testid="twin-tile-service-disposition"
          icon="constellation"
        />
        <Tile
          to="/twin/landscape/BIAN"
          name={t("modules.twin.home.tiles.landscape.name")}
          desc={t("modules.twin.home.tiles.landscape.desc")}
          testid="twin-tile-landscape"
          icon="capability-map"
        />
        <Tile
          to="/twin/landscape/BIAN/classic"
          name={t("modules.twin.home.tiles.landscapeClassic.name")}
          desc={t("modules.twin.home.tiles.landscapeClassic.desc")}
          testid="twin-tile-landscape-classic"
          icon="capability-map"
        />
        <Tile
          to="/twin/capmap"
          name={t("modules.twin.home.tiles.capmap.name")}
          desc={t("modules.twin.home.tiles.capmap.desc")}
          testid="twin-tile-capmap"
          icon="capability-map"
        />
        <Tile
          to="/twin/constellation"
          name={t("modules.twin.home.tiles.constellation.name")}
          desc={t("modules.twin.home.tiles.constellation.desc")}
          testid="twin-tile-constellation"
          icon="constellation"
        />
      </TileSection>

      <TileSection
        heading={t("modules.twin.home.sections.governance")}
        testid="twin-section-governance"
      >
        <Tile
          to="/board"
          name={t("modules.twin.home.tiles.board.name")}
          desc={t("modules.twin.home.tiles.board.desc")}
          testid="twin-tile-board"
          icon="dossier"
          visible={isAdmin}
        />
        <Tile
          to="/twin/ibs-admission"
          name={t("modules.twin.home.tiles.ibsAdmission.name")}
          desc={t("modules.twin.home.tiles.ibsAdmission.desc")}
          testid="twin-tile-ibs-admission"
          icon="ibs"
          visible={readsCeremony}
        />
        <Tile
          to="/twin/cifs"
          name={t("modules.twin.home.tiles.cifs.name")}
          desc={t("modules.twin.home.tiles.cifs.desc")}
          testid="twin-tile-cifs"
          icon="ibs"
          visible={readsCeremony}
        />
        <Tile
          to="/twin/mvb-candidates"
          name={t("modules.twin.home.tiles.mvbCandidates.name")}
          desc={t("modules.twin.home.tiles.mvbCandidates.desc")}
          testid="twin-tile-mvb-candidates"
          icon="mvb"
          visible={readsCeremony}
        />
        <Tile
          to="/twin/mvb-recovery"
          name="MVB recovery sequence"
          desc="Ordered restoration plan, critical path + time to minimum viability"
          testid="twin-tile-mvb-recovery"
          icon="mvb"
        />
        <Tile
          to="/twin/continuity-posture"
          name="Continuity posture"
          desc="What continuity capability each MVB-supporting service has, proven when"
          testid="twin-tile-continuity-posture"
          icon="mvb"
        />
        <Tile
          to="/twin/mvb-drill"
          name="MVB validation drill"
          desc="Rehearse the recovery sequence; record actual vs projected per step"
          testid="twin-tile-mvb-drill"
          icon="drill"
        />
        <Tile
          to="/twin/ibs-portfolio"
          name={t("modules.twin.home.tiles.ibsPortfolio.name")}
          desc={t("modules.twin.home.tiles.ibsPortfolio.desc")}
          testid="twin-tile-ibs-portfolio"
          icon="ibs"
        />
        <Tile
          to="/twin/ibs-groups"
          name={t("modules.twin.home.tiles.ibsGroups.name")}
          desc={t("modules.twin.home.tiles.ibsGroups.desc")}
          testid="twin-tile-ibs-groups"
          icon="ibs"
        />
        <Tile
          to="/twin/findings/new"
          name={t("modules.twin.home.tiles.raiseFinding.name")}
          desc={t("modules.twin.home.tiles.raiseFinding.desc")}
          testid="twin-tile-raise-finding"
          icon="finding"
          visible={canRunOperations(me.data)}
        />
        <Tile
          to="/readiness"
          name={t("modules.twin.home.tiles.readiness.name")}
          desc={t("modules.twin.home.tiles.readiness.desc")}
          testid="twin-tile-readiness"
          icon="benchmark"
          visible={canReadReadiness(me.data)}
        />
        <Tile
          to="/twin/field-exam"
          name={t("modules.twin.home.tiles.fieldExam.name")}
          desc={t("modules.twin.home.tiles.fieldExam.desc")}
          testid="twin-tile-field-exam"
          icon="verifier"
          visible={isAdmin}
        />
        <Tile
          to="/twin/drills"
          name={t("modules.twin.home.tiles.drills.name")}
          desc={t("modules.twin.home.tiles.drills.desc")}
          testid="twin-tile-drills"
          icon="drill"
        />
        <Tile
          to="/twin/war-room"
          name={t("modules.twin.home.tiles.warRoom.name")}
          desc={t("modules.twin.home.tiles.warRoom.desc")}
          testid="twin-tile-war-room"
          icon="war-room"
        />
        <Tile
          to="/twin/fragility"
          name={t("modules.twin.home.tiles.fragility.name")}
          desc={t("modules.twin.home.tiles.fragility.desc")}
          testid="twin-tile-fragility"
          icon="fragility-radar"
        />
      </TileSection>
    </article>
  );
}
